import { useState, useEffect } from 'react';
import { Table, Button, message, Tag, Modal, Input, Breadcrumb, Tabs, DatePicker, Space, Select } from 'antd';
import { HomeOutlined, CheckOutlined, CloseOutlined, DownloadOutlined, EyeOutlined, SearchOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const { TextArea } = Input;

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || fallback;

const LeaveManagementPage = () => {
  const [allRequests, setAllRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [remark, setRemark] = useState('');
  const [approving, setApproving] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [pendingSelectedKeys, setPendingSelectedKeys] = useState([]);
  const [pendingClassFilter, setPendingClassFilter] = useState(null);
  const [pendingSearchText, setPendingSearchText] = useState('');
  const [filteredPendingRequests, setFilteredPendingRequests] = useState([]);
  const [batchApproving, setBatchApproving] = useState(false);
  const [userRole, setUserRole] = useState('');
  // 新增筛选条件
  const [classFilter, setClassFilter] = useState(null);
  const [overnightFilter, setOvernightFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelRecord, setCancelRecord] = useState(null);
  const [cancelReason, setCancelReason] = useState("管理员手动撤销");
  const [cancelling, setCancelling] = useState(false);

  // 获取当前用户角色
  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    setUserRole(userInfo.role || '');
  }, []);

  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/leave/all', { params: { page: 1, pageSize: 1000 } });
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.records)
          ? response.data.records
          : [];
      setAllRequests(data);
      setFilteredRequests(data);
    } catch (error) {
      message.error('获取请假记录失败');
    }
    setLoading(false);
  };

  // 综合筛选函数 - 同时应用所有筛选条件
  const applyFilters = (data, dates, search, classVal, overnightVal, statusVal) => {
    let filtered = data;
    
    // 日期范围筛选
    if (dates && dates.length === 2) {
      const startStr = dates[0].format('YYYY-MM-DD');
      const endStr = dates[1].format('YYYY-MM-DD');
      filtered = filtered.filter(r => {
        const leaveStart = r.leaveDate;
        const leaveEnd = r.endDate || r.leaveDate;
        return leaveStart <= endStr && leaveEnd >= startStr;
      });
    }
    
    // 搜索筛选（姓名）
    if (search && search.trim()) {
      const keyword = search.trim().toLowerCase();
      filtered = filtered.filter(r => {
        const name = (r.student?.name || '').toLowerCase();
        return name.includes(keyword);
      });
    }
    
    // 班级筛选
    if (classVal) {
      filtered = filtered.filter(r => r.student?.className === classVal);
    }
    
    // 过夜假筛选 - 使用动态计算，根据查询日期判断
    if (overnightVal !== null && overnightVal !== undefined) {
      const queryDate = dates ? dates[0].format('YYYY-MM-DD') : null;
      filtered = filtered.filter(r => {
        // 动态判断：结束日期 > 查询日期 则为过夜假
        const leaveEnd = r.endDate || r.leaveDate;
        const isOvernight = queryDate ? leaveEnd > queryDate : r.overnight;
        return isOvernight === overnightVal;
      });
    }
    
    // 状态筛选
    if (statusVal) {
      filtered = filtered.filter(r => r.status === statusVal);
    }
    
    return filtered;
  };

  // 获取班级列表
  const getClassList = () => {
    const classes = [...new Set(allRequests.map(r => r.student?.className).filter(Boolean))];
    return classes.map(c => ({ label: c, value: c }));
  };

  // 统一触发筛选
  const triggerFilter = (dates, search, classVal, overnightVal, statusVal) => {
    setFilteredRequests(applyFilters(allRequests, dates, search, classVal, overnightVal, statusVal));
    setSelectedRowKeys([]);
  };

  // 日期范围筛选
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    triggerFilter(dates, searchText, classFilter, overnightFilter, statusFilter);
  };

  // 搜索筛选
  const handleSearch = (value) => {
    setSearchText(value);
    triggerFilter(dateRange, value, classFilter, overnightFilter, statusFilter);
  };

  // 班级筛选
  const handleClassFilter = (value) => {
    setClassFilter(value);
    triggerFilter(dateRange, searchText, value, overnightFilter, statusFilter);
  };

  // 过夜假筛选
  const handleOvernightFilter = (value) => {
    setOvernightFilter(value);
    triggerFilter(dateRange, searchText, classFilter, value, statusFilter);
  };

  // 状态筛选
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    triggerFilter(dateRange, searchText, classFilter, overnightFilter, value);
  };

  // 导出Excel
  const handleExport = async () => {
    // 优先导出选中的记录，否则导出筛选后的记录
    let dataToExport;
    if (selectedRowKeys.length > 0) {
      dataToExport = filteredRequests.filter(r => selectedRowKeys.includes(r.id));
    } else {
      // 始终使用筛选后的数据
      dataToExport = filteredRequests;
    }
    if (dataToExport.length === 0) {
      message.warning('没有可导出的数据，请先选择日期范围');
      return;
    }
    
    try {
      message.loading({ content: '正在生成导出文件...', key: 'exporting' });
      const ids = dataToExport.map(r => r.id);
      const response = await api.post('/leave/export', ids, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const dateStr = dateRange ? `${dateRange[0].format('YYYYMMDD')}-${dateRange[1].format('YYYYMMDD')}` : dayjs().format('YYYYMMDD');
      link.setAttribute('download', `请假记录_${dateStr}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success({ content: '导出成功', key: 'exporting' });
    } catch (error) {
      console.error(error);
      message.error({ content: '导出失败', key: 'exporting' });
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await api.get('/leave/pending', { params: { page: 1, pageSize: 1000 } });
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.records)
          ? response.data.records
          : [];
      setPendingRequests(data);
      setFilteredPendingRequests(data);
    } catch (error) {
      console.error('获取待审批请假失败:', error);
    }
  };

  // 获取待审批记录中的班级列表
  const getPendingClassList = () => {
    const classes = [...new Set(pendingRequests.map(r => r.student?.className).filter(Boolean))];
    return classes.map(c => ({ label: c, value: c }));
  };

  // 待审批综合筛选
  const applyPendingFilters = (data, classFilter, search) => {
    let filtered = data;
    if (classFilter) {
      filtered = filtered.filter(r => r.student?.className === classFilter);
    }
    if (search && search.trim()) {
      const keyword = search.trim().toLowerCase();
      filtered = filtered.filter(r => {
        const name = (r.student?.name || '').toLowerCase();
        const className = (r.student?.className || '').toLowerCase();
        return name.includes(keyword) || className.includes(keyword);
      });
    }
    return filtered;
  };

  // 待审批班级筛选
  const handlePendingClassFilter = (value) => {
    setPendingClassFilter(value);
    setFilteredPendingRequests(applyPendingFilters(pendingRequests, value, pendingSearchText));
    setPendingSelectedKeys([]);
  };

  // 待审批搜索
  const handlePendingSearch = (value) => {
    setPendingSearchText(value);
    setFilteredPendingRequests(applyPendingFilters(pendingRequests, pendingClassFilter, value));
    setPendingSelectedKeys([]);
  };

  // 批量审批
  const handleBatchApprove = async (approved) => {
    if (pendingSelectedKeys.length === 0) {
      message.warning('请先选择要审批的记录');
      return;
    }
    setBatchApproving(true);
    try {
      // 逐个审批
      for (const id of pendingSelectedKeys) {
        await api.post(`/leave/${id}/approve`, { approved, remark: '' });
      }
      message.success(`已${approved ? '批准' : '拒绝'} ${pendingSelectedKeys.length} 条请假申请`);
      setPendingSelectedKeys([]);
      fetchAllRequests();
      fetchPendingRequests();
    } catch (error) {
      message.error('批量审批失败');
    }
    setBatchApproving(false);
  };

  useEffect(() => {
    fetchAllRequests();
    fetchPendingRequests();
  }, []);

  const showApproveModal = (record) => {
    setSelectedRequest(record);
    setRemark('');
    setIsModalVisible(true);
  };

  const handleApprove = async (approved) => {
    if (!selectedRequest) return;
    
    // 班主任审批过夜假时必须填写审批意见
    if (userRole === 'CLASS_TEACHER' && selectedRequest.overnight && !remark.trim()) {
      message.warning('过夜假审批必须填写审批意见');
      return;
    }
    
    setApproving(true);
    try {
      await api.post(`/leave/${selectedRequest.id}/approve`, {
        approved,
        remark
      });
      message.success(approved ? '已批准请假申请' : '已拒绝请假申请');
      setIsModalVisible(false);
      fetchAllRequests();
      fetchPendingRequests();
    } catch (error) {
      message.error('操作失败');
    }
    setApproving(false);
  };

  const refreshRequestsAfterMutation = async () => {
    setSelectedRowKeys([]);
    setPendingSelectedKeys([]);
    await Promise.all([fetchAllRequests(), fetchPendingRequests()]);
  };

  const showAdminCancelModal = (record) => {
    setCancelRecord(record);
    setCancelReason("管理员手动撤销");
    setCancelModalVisible(true);
  };

  const handleAdminCancel = async () => {
    if (!cancelRecord) return;
    if (!cancelReason.trim()) {
      message.warning("请填写撤销原因");
      return;
    }

    setCancelling(true);
    try {
      await api.post(`/leave/${cancelRecord.id}/admin-cancel`, { reason: cancelReason.trim() });
      message.success("请假申请已变更为已撤销");
      setCancelModalVisible(false);
      if (viewRecord?.id === cancelRecord.id) {
        setViewModalVisible(false);
        setViewRecord(null);
      }
      await refreshRequestsAfterMutation();
    } catch (error) {
      message.error(getErrorMessage(error, "撤销请假失败"));
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteRequest = (record) => {
    const studentName = record.student?.name || "未知学员";
    const start = formatDateTime(record.leaveDate, record.startTime || '08:00');
    const end = formatDateTime(record.endDate || record.leaveDate, record.endTime || '18:00');

    Modal.confirm({
      title: "永久删除请假记录？",
      content: `确定删除 ${studentName} 从 ${start} 至 ${end} 的请假记录吗？此操作不可恢复。`,
      okText: "永久删除",
      okType: 'danger',
      cancelText: "取消",
      async onOk() {
        try {
          await api.delete(`/leave/${record.id}`);
          message.success("请假记录已永久删除");
          if (viewRecord?.id === record.id) {
            setViewModalVisible(false);
            setViewRecord(null);
          }
          await refreshRequestsAfterMutation();
        } catch (error) {
          message.error(getErrorMessage(error, "删除请假记录失败"));
          throw error;
        }
      },
    });
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'PENDING':
        return <Tag color="orange">待教师审批</Tag>;
      case 'TEACHER_APPROVED':
        return <Tag color="blue">待管理员审批</Tag>;
      case 'APPROVED':
        return <Tag color="green">已批准</Tag>;
      case 'REJECTED':
        return <Tag color="red">已拒绝</Tag>;
      case 'CANCELLED':
        return <Tag>已撤销</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };



  const getDetailStatusMeta = (status) => {
    switch (status) {
      case 'CANCELLED':
        return {
          label: '已撤销',
          description: '该请假申请已被撤销',
          background: 'linear-gradient(135deg, #f5f5f5 0%, #d9d9d9 100%)',
          color: '#595959',
        };
      case 'REJECTED':
        return {
          label: '已拒绝',
          description: '该请假申请未通过',
          background: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
          color: '#ff4d4f',
        };
      case 'TEACHER_APPROVED':
        return {
          label: '待管理员审批',
          description: '请耐心等待审批结果',
          background: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
          color: '#1677ff',
        };
      default:
        return {
          label: '待教师审批',
          description: '请耐心等待审批结果',
          background: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
          color: '#fa8c16',
        };
    }
  };

  const formatDateTime = (dateStr, time) => {
    if (!dateStr) return '-';
    return `${dateStr} ${time}`;
  };

  // 根据查询日期动态判断是否为过夜假
  // 逻辑：如果请假结束日期 > 查询日期，则为过夜假；否则为普通假
  const isOvernightForDate = (record, queryDate) => {
    if (!queryDate) return record.overnight; // 没有选择日期时使用原始值
    const leaveEnd = record.endDate || record.leaveDate;
    return leaveEnd > queryDate;
  };

  // 下载请假条为图片
  const handleDownloadLeaveSlip = async () => {
    if (!viewRecord) return;
    setDownloading(true);
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const element = document.getElementById('leave-slip-content');
      if (!element) {
        message.error('无法获取请假条内容');
        setDownloading(false);
        return;
      }
      
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      
      const link = document.createElement('a');
      link.download = `请假条_${viewRecord.student?.name || '未知'}_${viewRecord.leaveDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      message.success('下载成功');
    } catch (error) {
      console.error('下载失败:', error);
      message.error('下载失败，请重试');
    }
    setDownloading(false);
  };

  const columns = [
    {
      title: '学员',
      dataIndex: ['student', 'name'],
      key: 'studentName',
      width: 80,
    },
    {
      title: '班级',
      dataIndex: ['student', 'className'],
      key: 'className',
      width: 80,
    },
    {
      title: '楼栋',
      dataIndex: 'building',
      key: 'building',
      width: 70,
      render: (text) => text || '-',
    },
    {
      title: '寝室号',
      dataIndex: 'dormitory',
      key: 'dormitory',
      width: 80,
      render: (text) => text || '-',
    },
    {
      title: '开始时间',
      key: 'startTime',
      width: 140,
      render: (_, record) => formatDateTime(record.leaveDate, record.startTime || '08:00'),
    },
    {
      title: '结束时间',
      key: 'endTime',
      width: 140,
      render: (_, record) => formatDateTime(record.endDate || record.leaveDate, record.endTime || '18:00'),
    },
    {
      title: '请假原因',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '过夜假',
      dataIndex: 'overnight',
      key: 'overnight',
      width: 80,
      render: (_, record) => {
        // 根据选择的日期范围动态判断（使用开始日期作为查询日期）
        const queryDate = dateRange ? dateRange[0].format('YYYY-MM-DD') : null;
        const isOvernight = isOvernightForDate(record, queryDate);
        return isOvernight ? (
          <span style={{ 
            display: 'inline-block',
            padding: '2px 8px', 
            background: '#faad14', 
            borderRadius: '4px',
            color: '#fff',
            fontSize: '12px',
            whiteSpace: 'nowrap'
          }}>过夜假</span>
        ) : (
          <span style={{ 
            display: 'inline-block',
            padding: '2px 8px', 
            background: '#8c8c8c', 
            borderRadius: '4px',
            color: '#fff',
            fontSize: '12px',
            whiteSpace: 'nowrap'
          }}>普通假</span>
        );
      },
    },
    {
      title: '家长联系方式',
      dataIndex: 'parentPhone',
      key: 'parentPhone',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => getStatusTag(status),
      filters: [
        { text: '待教师审批', value: 'PENDING' },
        { text: '待管理员审批', value: 'TEACHER_APPROVED' },
        { text: '已批准', value: 'APPROVED' },
        { text: '已拒绝', value: 'REJECTED' },
        { text: '已撤销', value: 'CANCELLED' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: '操作',
      key: 'action',
      width: userRole === 'ADMIN' ? 210 : 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4} wrap>
          <Button
            type="link"
            icon={<EyeOutlined />}
            aria-label="查看请假详情"
            onClick={() => { setViewRecord(record); setViewModalVisible(true); }}
            style={{ padding: 0 }}
          />
          {(userRole === 'CLASS_TEACHER' && record.status === 'PENDING') ||
           ((userRole === 'ADMIN' || userRole === 'TEACHING_ADMIN') && record.status === 'TEACHER_APPROVED') ? (
            <Button type="link" onClick={() => showApproveModal(record)} style={{ padding: 0 }}>
              审批
            </Button>
          ) : record.status === 'PENDING' ? (
            <span style={{ color: '#999', fontSize: 12 }}>待教师审批</span>
          ) : record.status === 'TEACHER_APPROVED' ? (
            <span style={{ color: '#1890ff', fontSize: 12 }}>待管理员审批</span>
          ) : (
            <span style={{ color: '#52c41a', fontSize: 12 }}>已处理</span>
          )}
          {userRole === 'ADMIN' && record.status !== 'CANCELLED' && (
            <Button
              type="link"
              icon={<StopOutlined />}
              onClick={() => showAdminCancelModal(record)}
              style={{ padding: 0 }}
            >
              撤销
            </Button>
          )}
          {userRole === 'ADMIN' && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteRequest(record)}
              style={{ padding: 0 }}
            >
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const items = [
    {
      key: 'pending',
      label: `待审批 (${pendingRequests.length})`,
      children: (
        <>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <Space wrap>
              <Space.Compact>
                <Input
                  placeholder="搜索姓名/班级"
                  allowClear
                  value={pendingSearchText}
                  onChange={(e) => {
                    setPendingSearchText(e.target.value);
                    if (!e.target.value) handlePendingSearch('');
                  }}
                  onPressEnter={() => handlePendingSearch(pendingSearchText)}
                  style={{ width: 120 }}
                  prefix={<SearchOutlined />}
                />
                <Button aria-label="搜索待审批请假" icon={<SearchOutlined />} onClick={() => handlePendingSearch(pendingSearchText)} />
              </Space.Compact>
              <Select
                placeholder="筛选班级"
                allowClear
                style={{ width: 140 }}
                options={getPendingClassList()}
                value={pendingClassFilter}
                onChange={handlePendingClassFilter}
              />
              {pendingSelectedKeys.length > 0 && (
                <Tag color="blue">已选择 {pendingSelectedKeys.length} 条</Tag>
              )}
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<CheckOutlined />} 
                onClick={() => handleBatchApprove(true)}
                loading={batchApproving}
                disabled={pendingSelectedKeys.length === 0}
              >
                批量批准{pendingSelectedKeys.length > 0 ? `(${pendingSelectedKeys.length})` : ''}
              </Button>
              <Button 
                danger 
                icon={<CloseOutlined />} 
                onClick={() => handleBatchApprove(false)}
                loading={batchApproving}
                disabled={pendingSelectedKeys.length === 0}
              >
                批量拒绝{pendingSelectedKeys.length > 0 ? `(${pendingSelectedKeys.length})` : ''}
              </Button>
            </Space>
          </div>
          <Table
            columns={columns}
            dataSource={filteredPendingRequests}
            loading={loading}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: pendingSelectedKeys,
              onChange: (keys) => setPendingSelectedKeys(keys),
              getCheckboxProps: (record) => ({
                // 教师只能选择PENDING状态，管理员只能选择TEACHER_APPROVED状态
                disabled: userRole === 'CLASS_TEACHER' 
                  ? record.status !== 'PENDING' 
                  : record.status !== 'TEACHER_APPROVED',
              }),
            }}
            pagination={{ defaultPageSize: 10, showTotal: (total) => `共 ${total} 条` }}
            scroll={{ x: userRole === 'ADMIN' ? 1310 : 1210 }}
          />
        </>
      ),
    },
    {
      key: 'all',
      label: '全部记录',
      children: (
        <>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <Space wrap>
              <Space.Compact>
                <Input
                  placeholder="搜索姓名"
                  allowClear
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    if (!e.target.value) handleSearch('');
                  }}
                  onPressEnter={() => handleSearch(searchText)}
                  style={{ width: 100 }}
                  prefix={<SearchOutlined />}
                />
                <Button aria-label="搜索请假记录" icon={<SearchOutlined />} onClick={() => handleSearch(searchText)} />
              </Space.Compact>
              <RangePicker 
                value={dateRange}
                onChange={handleDateRangeChange}
                allowClear
                placeholder={['开始日期', '结束日期']}
              />
              <Select
                placeholder="筛选班级"
                allowClear
                style={{ width: 120 }}
                options={getClassList()}
                value={classFilter}
                onChange={handleClassFilter}
              />
              <Select
                placeholder="过夜假"
                allowClear
                style={{ width: 100 }}
                options={[
                  { label: '过夜假', value: true },
                  { label: '普通假', value: false },
                ]}
                value={overnightFilter}
                onChange={handleOvernightFilter}
              />
              <Select
                placeholder="状态"
                allowClear
                style={{ width: 130 }}
                options={[
                  { label: '待教师审批', value: 'PENDING' },
                  { label: '待管理员审批', value: 'TEACHER_APPROVED' },
                  { label: '已批准', value: 'APPROVED' },
                  { label: '已拒绝', value: 'REJECTED' },
                  { label: '已撤销', value: 'CANCELLED' },
                ]}
                value={statusFilter}
                onChange={handleStatusFilter}
              />
              {selectedRowKeys.length > 0 && (
                <Tag color="blue">已选择 {selectedRowKeys.length} 条</Tag>
              )}
            </Space>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
              {selectedRowKeys.length > 0 ? `导出选中(${selectedRowKeys.length})` : '导出'}
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={filteredRequests}
            loading={loading}
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
            pagination={{ defaultPageSize: 10, showTotal: (total) => `共 ${total} 条` }}
            scroll={{ x: userRole === 'ADMIN' ? 1310 : 1210 }}
          />
        </>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Breadcrumb items={[
          { title: <><HomeOutlined /> 首页</> },
          { title: '请假管理' },
        ]} />
        <h2 className="page-title">请假管理</h2>
      </div>

      <div className="table-card">
        <Tabs defaultActiveKey="pending" items={items} />
      </div>

      {/* 查看请假条/详情弹窗 */}
      <Modal
        title={viewRecord?.status === 'APPROVED' ? '请假条' : '请假详情'}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={
          <Space>
            {viewRecord?.status === 'APPROVED' && (
              <Button icon={<DownloadOutlined />} onClick={handleDownloadLeaveSlip} loading={downloading}>
                下载图片
              </Button>
            )}
            <Button onClick={() => setViewModalVisible(false)}>关闭</Button>
          </Space>
        }
        width={420}
      >
        {viewRecord && viewRecord.status === 'APPROVED' ? (
          <div id="leave-slip-content" style={{ background: '#fff', position: 'relative', padding: '16px', overflow: 'hidden', minHeight: '500px' }}>
            {/* 水印 - 仅已批准状态显示 */}
            {viewRecord.adminApprovedAt && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                transform: 'rotate(-25deg)',
                pointerEvents: 'none',
                zIndex: 1,
                overflow: 'hidden'
              }}>
                {[-2, -1, 0, 1, 2].map(row => (
                  <div key={row} style={{ 
                    fontSize: '14px', 
                    color: 'rgba(102, 102, 102, 0.3)', 
                    fontWeight: 'bold', 
                    whiteSpace: 'nowrap',
                    marginBottom: '60px'
                  }}>
                    审批通过 {viewRecord.adminApprovedAt.split('T')[0]} {viewRecord.adminApprovedAt.split('T')[1]?.split('.')[0] || ''}
                  </div>
                ))}
              </div>
            )}

            {/* 标题区域 */}
            <div style={{ textAlign: 'center', paddingBottom: '12px', borderBottom: '2px dashed #e8e8e8', marginBottom: '12px', position: 'relative', zIndex: 2 }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333', letterSpacing: '8px', marginBottom: '4px' }}>请 假 条</div>
              <div style={{ fontSize: '11px', color: '#999' }}>鸿仁云服务</div>
            </div>

            {/* 状态印章 */}
            <div style={{
              position: 'absolute',
              top: '50px',
              right: '20px',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              transform: 'rotate(15deg)',
              opacity: 0.8,
              border: '3px solid #52c41a',
              color: '#52c41a',
              zIndex: 3
            }}>
              已批准
            </div>

            {/* 过夜假/普通假印章 */}
            <div style={{
              position: 'absolute',
              right: '20px',
              top: '130px',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              transform: 'rotate(-15deg)',
              opacity: 0.85,
              border: `3px solid ${viewRecord.overnight ? '#ff4d4f' : '#8c8c8c'}`,
              color: viewRecord.overnight ? '#ff4d4f' : '#8c8c8c',
              zIndex: 3
            }}>
              {viewRecord.overnight ? '过夜假' : '普通假'}
            </div>

            {/* 内容区域 */}
            <div style={{ padding: '4px 0', position: 'relative', zIndex: 2 }}>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: '14px', color: '#666', width: '90px', flexShrink: 0 }}>姓　　名：</span>
                <span style={{ fontSize: '14px', color: '#333' }}>{viewRecord.student?.name || '-'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: '14px', color: '#666', width: '90px', flexShrink: 0 }}>所属班级：</span>
                <span style={{ fontSize: '14px', color: '#333' }}>{viewRecord.student?.className || '-'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: '14px', color: '#666', width: '90px', flexShrink: 0 }}>专　　业：</span>
                <span style={{ fontSize: '14px', color: '#333' }}>{viewRecord.student?.major || '未填写'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: '14px', color: '#666', width: '90px', flexShrink: 0 }}>楼　　栋：</span>
                <span style={{ fontSize: '14px', color: '#333' }}>{viewRecord.building || '未填写'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: '14px', color: '#666', width: '90px', flexShrink: 0 }}>寝 室 号：</span>
                <span style={{ fontSize: '14px', color: '#333' }}>{viewRecord.dormitory || '未填写'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: '14px', color: '#666', width: '90px', flexShrink: 0 }}>家长电话：</span>
                <span style={{ fontSize: '14px', color: '#333' }}>{viewRecord.parentPhone || '未填写'}</span>
              </div>
              <div style={{ padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>请假时间：</span>
                <div style={{ paddingLeft: '90px', marginTop: '-20px' }}>
                  <div style={{ fontSize: '14px', color: '#333' }}>{viewRecord.leaveDate} {viewRecord.startTime || '08:00'}</div>
                  <div style={{ fontSize: '14px', color: '#999', padding: '2px 0 2px 16px' }}>至</div>
                  <div style={{ fontSize: '14px', color: '#333' }}>{viewRecord.endDate || viewRecord.leaveDate} {viewRecord.endTime || '18:00'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontSize: '14px', color: '#666', width: '90px', flexShrink: 0 }}>请假事由：</span>
                <span style={{ fontSize: '14px', color: '#333' }}>{viewRecord.reason || '无'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0' }}>
                <span style={{ fontSize: '14px', color: '#666', width: '90px', flexShrink: 0 }}>申请时间：</span>
                <span style={{ fontSize: '14px', color: '#333' }}>{viewRecord.createdAt ? viewRecord.createdAt.split('T')[0] + ' ' + (viewRecord.createdAt.split('T')[1]?.split('.')[0] || '') : '-'}</span>
              </div>
            </div>

            {/* 审批信息 */}
            {(viewRecord.teacherApproverName || viewRecord.adminApproverName) && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '2px dashed #e8e8e8', position: 'relative', zIndex: 2 }}>
                <div style={{ fontSize: '16px', fontWeight: 500, color: '#333', marginBottom: '8px' }}>审批信息</div>
                {viewRecord.teacherApproverName && (
                  <div style={{ background: '#fafafa', borderRadius: '4px', padding: '8px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', padding: '2px 0' }}>
                      <span style={{ fontSize: '13px', color: '#666', width: '90px' }}>教师审批：</span>
                      <span style={{ fontSize: '13px', color: '#52c41a', fontWeight: 500 }}>已批准</span>
                    </div>
                    <div style={{ display: 'flex', padding: '2px 0' }}>
                      <span style={{ fontSize: '13px', color: '#666', width: '90px' }}>审批人：</span>
                      <span style={{ fontSize: '13px', color: '#333' }}>{viewRecord.teacherApproverName}</span>
                    </div>
                    {viewRecord.teacherApprovedAt && (
                      <div style={{ display: 'flex', padding: '2px 0' }}>
                        <span style={{ fontSize: '13px', color: '#666', width: '90px' }}>审批时间：</span>
                        <span style={{ fontSize: '13px', color: '#333' }}>{viewRecord.teacherApprovedAt.split('T')[0]} {viewRecord.teacherApprovedAt.split('T')[1]?.split('.')[0] || ''}</span>
                      </div>
                    )}
                  </div>
                )}
                {viewRecord.adminApproverName && (
                  <div style={{ background: '#fafafa', borderRadius: '4px', padding: '8px' }}>
                    <div style={{ display: 'flex', padding: '2px 0' }}>
                      <span style={{ fontSize: '13px', color: '#666', width: '90px' }}>管理员审批：</span>
                      <span style={{ fontSize: '13px', color: '#52c41a', fontWeight: 500 }}>已批准</span>
                    </div>
                    <div style={{ display: 'flex', padding: '2px 0' }}>
                      <span style={{ fontSize: '13px', color: '#666', width: '90px' }}>审批人：</span>
                      <span style={{ fontSize: '13px', color: '#333' }}>{viewRecord.adminApproverName}</span>
                    </div>
                    {viewRecord.adminApprovedAt && (
                      <div style={{ display: 'flex', padding: '2px 0' }}>
                        <span style={{ fontSize: '13px', color: '#666', width: '90px' }}>审批时间：</span>
                        <span style={{ fontSize: '13px', color: '#333' }}>{viewRecord.adminApprovedAt.split('T')[0]} {viewRecord.adminApprovedAt.split('T')[1]?.split('.')[0] || ''}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 底部说明 */}
            <div style={{ marginTop: '16px', paddingTop: '8px', borderTop: '1px solid #f0f0f0', textAlign: 'center', position: 'relative', zIndex: 2 }}>
              <div style={{ fontSize: '10px', color: '#bbb', lineHeight: 1.6 }}>此请假条由系统自动生成</div>
              <div style={{ fontSize: '10px', color: '#bbb', lineHeight: 1.6 }}>仅供内部使用</div>
            </div>
          </div>
        ) : viewRecord && (
          /* 未审批/已拒绝状态：显示详情视图 */
          <div style={{ padding: '8px 0' }}>
            {/* 状态标签 */}
            <div style={{ 
              padding: '16px', 
              borderRadius: '8px', 
              marginBottom: '16px',
              background: getDetailStatusMeta(viewRecord.status).background,
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: getDetailStatusMeta(viewRecord.status).color
              }}>
                {getDetailStatusMeta(viewRecord.status).label}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {getDetailStatusMeta(viewRecord.status).description}
              </div>
            </div>

            {/* 申请详情 */}
            <div style={{ background: '#fafafa', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e8e8e8' }}>申请详情</div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>姓名</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.student?.name || '-'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>班级</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.student?.className || '-'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>寝室号</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.dormitory || '未填写'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>假期类型</span>
                <span style={{ color: viewRecord.overnight ? '#ff4d4f' : '#52c41a', flex: 1, textAlign: 'right' }}>{viewRecord.overnight ? '过夜假' : '普通假'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>开始时间</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.leaveDate} {viewRecord.startTime || '08:00'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>结束时间</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.endDate || viewRecord.leaveDate} {viewRecord.endTime || '18:00'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>请假事由</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.reason || '无'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>与学生关系</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.parentRelation || '未填写'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', width: '80px' }}>家长电话</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.parentPhone || '未填写'}</span>
              </div>
              <div style={{ display: 'flex', padding: '8px 0' }}>
                <span style={{ color: '#666', width: '80px' }}>申请时间</span>
                <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.createdAt ? viewRecord.createdAt.split('T')[0] + ' ' + (viewRecord.createdAt.split('T')[1]?.split('.')[0] || '') : '-'}</span>
              </div>
              {viewRecord.status === 'CANCELLED' && (
                <>
                  <div style={{ display: 'flex', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                    <span style={{ color: '#666', width: '80px' }}>撤销人</span>
                    <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.cancelledBy || '-'}</span>
                  </div>
                  <div style={{ display: 'flex', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                    <span style={{ color: '#666', width: '80px' }}>撤销时间</span>
                    <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>
                      {viewRecord.cancelledAt ? viewRecord.cancelledAt.replace('T', ' ').split('.')[0] : '-'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                    <span style={{ color: '#666', width: '80px' }}>撤销原因</span>
                    <span style={{ color: '#333', flex: 1, textAlign: 'right' }}>{viewRecord.cancelReason || '-'}</span>
                  </div>
                </>
              )}
            </div>

            {/* 审批流程 */}
            <div style={{ background: '#fafafa', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e8e8e8' }}>审批流程</div>
              
              {/* 教师审批 */}
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  background: viewRecord.teacherApproverName ? '#52c41a' : (viewRecord.status === 'PENDING' ? '#fa8c16' : '#e8e8e8'),
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  marginRight: '12px',
                  flexShrink: 0
                }}>1</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500 }}>教师审批</span>
                    <span style={{ 
                      color: viewRecord.teacherApproverName ? '#52c41a' : (viewRecord.status === 'REJECTED' && !viewRecord.adminApproverName ? '#ff4d4f' : (viewRecord.status === 'PENDING' ? '#fa8c16' : '#999'))
                    }}>
                      {viewRecord.teacherApproverName ? '✓ 已通过' : (viewRecord.status === 'REJECTED' && !viewRecord.adminApproverName ? '✕ 已拒绝' : (viewRecord.status === 'PENDING' ? '等待中' : '-'))}
                    </span>
                  </div>
                  {viewRecord.teacherApproverName && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      <div>审批人：{viewRecord.teacherApproverName}</div>
                      {viewRecord.teacherApprovedAt && <div>时间：{viewRecord.teacherApprovedAt.split('T')[0]} {viewRecord.teacherApprovedAt.split('T')[1]?.split('.')[0] || ''}</div>}
                      {viewRecord.teacherApprovalRemark && <div style={{ color: viewRecord.status === 'REJECTED' ? '#ff4d4f' : '#333' }}>{viewRecord.status === 'REJECTED' && !viewRecord.adminApproverName ? '拒绝原因' : '备注'}：{viewRecord.teacherApprovalRemark}</div>}
                    </div>
                  )}
                </div>
              </div>

              {/* 管理员审批 */}
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  background: viewRecord.adminApproverName ? (viewRecord.status === 'REJECTED' ? '#ff4d4f' : '#52c41a') : (viewRecord.status === 'TEACHER_APPROVED' ? '#fa8c16' : '#e8e8e8'),
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  marginRight: '12px',
                  flexShrink: 0
                }}>2</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500 }}>管理员审批</span>
                    <span style={{ 
                      color: viewRecord.status === 'APPROVED' ? '#52c41a' : (viewRecord.status === 'REJECTED' && viewRecord.adminApproverName ? '#ff4d4f' : (viewRecord.status === 'TEACHER_APPROVED' ? '#fa8c16' : '#999'))
                    }}>
                      {viewRecord.status === 'APPROVED' ? '✓ 已通过' : (viewRecord.status === 'REJECTED' && viewRecord.adminApproverName ? '✕ 已拒绝' : (viewRecord.status === 'TEACHER_APPROVED' ? '等待中' : '-'))}
                    </span>
                  </div>
                  {viewRecord.adminApproverName && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      <div>审批人：{viewRecord.adminApproverName}</div>
                      {viewRecord.adminApprovedAt && <div>时间：{viewRecord.adminApprovedAt.split('T')[0]} {viewRecord.adminApprovedAt.split('T')[1]?.split('.')[0] || ''}</div>}
                      {viewRecord.adminApprovalRemark && <div style={{ color: viewRecord.status === 'REJECTED' ? '#ff4d4f' : '#333' }}>{viewRecord.status === 'REJECTED' ? '拒绝原因' : '备注'}：{viewRecord.adminApprovalRemark}</div>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={`审批请假 - ${selectedRequest?.student?.name}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="reject"
            danger
            icon={<CloseOutlined />}
            loading={approving}
            onClick={() => handleApprove(false)}
          >
            拒绝
          </Button>,
          <Button
            key="approve"
            type="primary"
            icon={<CheckOutlined />}
            loading={approving}
            onClick={() => handleApprove(true)}
          >
            批准
          </Button>,
        ]}
      >
        {selectedRequest && (
          <div>
            <p><strong>班级：</strong>{selectedRequest.student?.className || '-'}</p>
            <p><strong>寝室号：</strong>{selectedRequest.dormitory || '-'}</p>
            <p><strong>请假时间：</strong>{selectedRequest.leaveDate} {selectedRequest.startTime || '08:00'} 至 {selectedRequest.endDate || selectedRequest.leaveDate} {selectedRequest.endTime || '18:00'}</p>
            <p><strong>请假原因：</strong>{selectedRequest.reason || '无'}</p>
            <p><strong>过夜假：</strong>{selectedRequest.overnight ? '是' : '否'}</p>
            <p><strong>与学生关系：</strong>{selectedRequest.parentRelation || '-'}</p>
            <p><strong>家长联系方式：</strong>{selectedRequest.parentPhone || '-'}</p>
            <p><strong>教师审批人：</strong>{selectedRequest.teacherApproverName || '-'}</p>
            <div style={{ marginTop: 16 }}>
              <p><strong>审批备注：</strong>{userRole === 'CLASS_TEACHER' && selectedRequest.overnight && <span style={{ color: '#ff4d4f' }}>（过夜假必填）</span>}</p>
              <TextArea
                rows={3}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder={userRole === 'CLASS_TEACHER' && selectedRequest.overnight ? '过夜假必须填写审批备注' : '可选填写审批备注'}
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={`撤销请假 - ${cancelRecord?.student?.name || ''}`}
        open={cancelModalVisible}
        onOk={handleAdminCancel}
        onCancel={() => {
          if (!cancelling) {
            setCancelModalVisible(false);
            setCancelRecord(null);
          }
        }}
        okText="确认撤销"
        cancelText="取消"
        confirmLoading={cancelling}
        destroyOnHidden
      >
        {cancelRecord && (
          <div>
            <p><strong>学员：</strong>{cancelRecord.student?.name || '-'}</p>
            <p>
              <strong>请假时间：</strong>
              {formatDateTime(cancelRecord.leaveDate, cancelRecord.startTime || '08:00')} 至{' '}
              {formatDateTime(cancelRecord.endDate || cancelRecord.leaveDate, cancelRecord.endTime || '18:00')}
            </p>
            <p><strong>当前状态：</strong>{getStatusTag(cancelRecord.status)}</p>
            <div style={{ marginTop: 16 }}>
              <p><strong>撤销原因：</strong><span style={{ color: '#ff4d4f' }}>（必填）</span></p>
              <TextArea
                rows={4}
                value={cancelReason}
                maxLength={500}
                showCount
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="请填写管理员撤销该请假的原因"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LeaveManagementPage;

